import React, { useCallback, useContext, useEffect,  useState } from "react";
import { AppContext } from "../../Context";
import {
  Box, Button, FormControl, OutlinedInput, InputAdornment,
  IconButton, Select, MenuItem, Stack
} from "@mui/material";
import {
  VM_Colors, VM_FormContainer, VM_ResponsiveBox, registrationStepShellSx,
} from "../../Styles/VMStyles";
import {
  ADD_BELOW_DETAILS, CONFORM_PASSWORD, PASSWORD,
  PERSONAL_INFORMATION, UPDATE_BELOW_DETAILS
} from "../../Util/VMConstants";
import TitleDesc from "../../Components/Custom/TitleDesc";
import OTPDialog from "./OTPDialog";
import axios from "axios";
import { toast } from "react-toastify";
import { isEmpty, toTitleCase } from "../../Util/UtilHelper";
import { API_USER_REGISTRATION, API_COUNTRIES } from "../../Util/URLConstants";
import { saveAuthFromResponse } from "../../Util/jwtAuth";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Loader from "../../Components/Loader";
import StyledInputField from "../../Components/Custom/StyledInputField";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import StorageFactory from "../../Util/StorageFactory";
import { useDispatch } from "react-redux";
import { clearSubscription } from "../../Redux/slices/subscriptionSlice";
import { clearSubscriptionTier } from "../../Redux/slices/subscriptionTierSlice";

export default function UserRegistration({ registerPending, purpose }) {
  const dispatch = useDispatch();
  const { formValues, handleChange, handleNext, variant } = useContext(AppContext);
  const { user_name, country_code, mobile_no, email, password, confirmpassword } = formValues;

  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    countryID: "",
    sCountryCode: "",
    countryList: [],
    countryError: "",
    showPassword: false,
    showConfirmPassword: false,
    passwordError: false,
    confirmPasswordError: false,
    loading: false,     // ✅ one loading state
    country_data: {}
  });

  const [showOtpDialog, setShowOtpDialog] = useState(false);

  // Clear cache, session, and in-memory subscription state on mount.
  // Keep JWT when continuing an incomplete login (venue / subvenue / otp).
  useEffect(() => {
    if (registerPending?.user_id) return;

    const sessionStore = new StorageFactory("session");
    const localStore = new StorageFactory("local");
    sessionStore.clear();
    localStore.clear();
    dispatch(clearSubscription());
    dispatch(clearSubscriptionTier());
  }, [dispatch, registerPending]);

  useEffect(()=>{
    if(registerPending.pending === 'otp'){
        formValues.user_id =  registerPending.user_id ;
        setShowOtpDialog(true);
      }

    // formValues.venue_id =  registerPending.venue_id ;
  }, [registerPending, formValues])

  // Fetch country list directly here
  useEffect(() => {
    const fetchCountryList = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const { data } = await axios.get(API_COUNTRIES);
        const list = data?.data || [];
        const defaultCountry = list.find((c) => c.cid === 101);

        setState((prev) => ({
          ...prev,
          countryList: list,
          countryID: defaultCountry?.cid || "",
          sCountryCode: defaultCountry?.country_code || "",
          country_data: defaultCountry || {},
          loading: false,
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          countryList: [],
          countryError: err.message || "Failed to load countries",
          loading: false,
        }));
        toast.error("Error fetching countries");
      }
    };
    fetchCountryList();
  }, []);

  // Validation
  const isError = useCallback(() => {
    return [user_name, country_code, mobile_no, email].some(
      (f) => (f.required && !f.value) || f.error
    );
  }, [user_name, country_code, mobile_no, email]);

  // Registration handler
  const handleRegistrationComplete = async () => {
    setState((prev) => ({
      ...prev,
      sCountryCodeError: isEmpty(prev.sCountryCode),
      passwordError: isEmpty(password.value),
      confirmPasswordError: isEmpty(confirmpassword.value),
    }));

    if (!state.sCountryCode || !password.value || !confirmpassword.value) return;
    if (password.value !== confirmpassword.value) return toast.error("Passwords do not match");
    if (password.value.length < 6) return toast.error("Password must be at least 6 characters");
    if (password.value.length > 25) return toast.error("Password must not exceed 25 characters");

    setState((prev) => ({ ...prev, loading: true }));
    try {
      const reqBody = {
        name: user_name.value,
        mobile_no: mobile_no.value,
        email: email.value,
        country_code: state.sCountryCode,
        is_otp_verified: false,
        firebase_token: "web",
        user_type_id: "001",
        password: password.value,
        country_data: state.country_data,
      };

      const { data: resp } = await axios.post(API_USER_REGISTRATION, reqBody, {
        headers: { "Content-Type": "application/json" },
      });

      setState((prev) => ({ ...prev, loading: false }));
      saveAuthFromResponse(resp);

      if (resp.status_code === 200) {
        formValues.user_id = resp.user_id;
        setShowOtpDialog(true);
      } else if (resp?.pending === "venue") {
        formValues.user_id = resp.user_id;
        handleNext();
      } else if (resp?.pending === "subvenue") {
        formValues.user_id = resp.user_id;
        formValues.venue_id = resp.venue_id;
        handleNext();
        handleNext();
      } else if (resp?.pending === "otp") {
        formValues.user_id = resp.user_id;
        setShowOtpDialog(true);
      } else if (resp?.pending === "completed") {
        toast.error(resp.message);
      } else {
        setShowOtpDialog(true);
      }
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false }));
      const resp = error?.response?.data;
      saveAuthFromResponse(resp);
      if (resp?.pending === "venue") {
        formValues.user_id = resp.user_id || resp.pendingdata?.user_id;
        handleNext();
        return;
      }
      if (resp?.pending === "subvenue") {
        formValues.user_id = resp.user_id || resp.pendingdata?.user_id;
        formValues.venue_id = resp.venue_id || resp.pendingdata?.venue_id;
        handleNext();
        handleNext();
        return;
      }
      if (resp?.pending === "otp") {
        formValues.user_id = resp.user_id || resp.pendingdata?.user_id;
        setShowOtpDialog(true);
        return;
      }
      toast.error(resp?.message || "Registration failed");
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        ...VM_ResponsiveBox,
        ...registrationStepShellSx,
      }}
    >
      <Box sx={VM_FormContainer}>
        <TitleDesc
          title={PERSONAL_INFORMATION}
          subtitle={purpose === "update" ? UPDATE_BELOW_DETAILS : ADD_BELOW_DETAILS}
        />

        {/* User Name */}
        <StyledInputField
          heading="Your Name"
          name="user_name"
          placeholder="Enter Your Name"
          variant={variant}
          value={user_name.value}
          onChange={handleChange}
          error={!!user_name.error}
          helperText={user_name.error}
          required={user_name.required}
          fullWidth
          style={{ mt: 1 }}
        />

        {/* COUNTRY + MOBILE */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            my: 3,
            width: "100%",
          }}
        >
          <Stack spacing={0.5} sx={{ minWidth: { sm: 200 } }}>
            <MuiTypographyMandatory label="Country" required />
            <Select
              value={state.countryID || ""}
              onChange={(e) => {
                const selectedCid = e.target.value;
                const selectedObj = state.countryList.find((c) => c.cid === selectedCid);
                setState((prev) => ({
                  ...prev,
                  countryID: selectedCid,
                  sCountryCode: selectedObj.country_code,
                  sCountryCodeError: false,
                  country_data: selectedObj,
                }));
              }}
              fullWidth
              displayEmpty
              sx={{ width: "100%", backgroundColor: VM_Colors.white }}
            >
              <MenuItem value="" disabled>  -- Select --   </MenuItem>
              {state.countryList.map((ctr) => (
                <MenuItem key={ctr.cid} value={ctr.cid}>
                  {ctr.country_name}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Box sx={{ width: "100%" }}>
            <StyledInputField
              heading={
                isEmpty(state.sCountryCode)
                  ? "Mobile No"
                  : `Mobile No (+${state.sCountryCode})`
              }
              name="mobile_no"
              placeholder="Enter Your Mobile Number"
              variant={variant}
              value={mobile_no.value}
              onChange={handleChange}
              error={!!mobile_no.error}
              helperText={mobile_no.error}
              required={mobile_no.required}
              fullWidth
              inputProps={{ maxLength: 15, inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </Box>
        </Box>

        {/* Email */}
        <StyledInputField
          heading="Email ID"
          name="email"
          placeholder="Enter Your Email Address"
          variant={variant}
          fullWidth
          style={{ mt: 1 }}
          value={email.value}
          onChange={handleChange}
          error={!!email.error}
          helperText={email.error}
          required={email.required}
        />

        {/* Passwords (only for new registration) */}
        {purpose !== "update" && (
          <>
            <FormControl sx={{ mt: 3, mb: 2, width: "100%" }} variant="outlined">
              <MuiTypographyMandatory label={PASSWORD} required />
              <OutlinedInput
                type={state.showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setState((prev) => ({ ...prev, showPassword: !prev.showPassword }))
                      }
                      edge="end"
                    >
                      {state.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                placeholder={toTitleCase("Enter your password")}
                value={password.value}
                onChange={(e) => {
                  password.value = e.target.value;
                  setState((prev) => ({
                    ...prev,
                    passwordError: isEmpty(password.value),
                  }));
                }}
                error={state.passwordError}
              />
            </FormControl>

            <FormControl sx={{ mt: 1, mb: 2, width: "100%" }} variant="outlined">
              <MuiTypographyMandatory label={CONFORM_PASSWORD} required />
              <OutlinedInput
                type={state.showConfirmPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setState((prev) => ({
                          ...prev,
                          showConfirmPassword: !prev.showConfirmPassword,
                        }))
                      }
                      edge="end"
                    >
                      {state.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                value={confirmpassword.value}
                placeholder={toTitleCase("Enter your password")}
                onChange={(e) => {
                  confirmpassword.value = e.target.value;
                  setState((prev) => ({
                    ...prev,
                    confirmPasswordError: isEmpty(confirmpassword.value),
                  }));
                }}
                error={state.confirmPasswordError}
              />
            </FormControl>
          </>
        )}

        {/* Submit */}
            {/* FIXED FOOTER */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          // height: FOOTER_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: VM_Colors.white,
          borderTop: "1px solid #e0e0e0",
          zIndex: 1200,
          padding:2
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={isError()}
          onClick={!isError() ? handleRegistrationComplete : undefined}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 2,
            minWidth: 200,
          }}
        >
          {purpose === "update" ? "Update" : "Next"}
        </Button>
      </Box>

       {
        showOtpDialog && 
        <OTPDialog
          showOtpDialog={showOtpDialog}
          handleClose={() => setShowOtpDialog(false)}
          arrived="userregistration"
          user_id={formValues.user_id}
          handleNext={handleNext}
        />
       }   

      </Box>

      {/* ✅ Unified Loader */}
      <Loader loading={state.loading} />
    </Box>
  );
}
